import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import omit from 'lodash/omit';
import { buttonContentContainerStyles, buttonStyles, caratStyles } from './Button.styles';
import IconArrowGrey from 'images/icons/arrow-grey.svg';

type ButtonType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'text-color'
  | 'text-color-outline'
  | 'text-color-light'
  | 'default'
  | 'warning'
  | 'alert-secondary'
  | 'primary-outline'
  | 'primary-inactive'
  | 'dark'
  | 'link'
  | 'text';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
type IconGap = '' | 'none' | 'xs' | 'sm';
type CaratPosition = 'start' | 'end';

type IconConfig = {
  src: string;
  width?: string;
  height?: string;
  alt?: string;
  loading?: 'lazy' | 'eager';
  fetchpriority?: 'high' | 'low' | 'auto';
  decoding?: 'sync' | 'async' | 'auto';
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  // Core behavior and appearance
  /** Button type/style variant */
  btnType?: ButtonType;
  /** Button size */
  size?: ButtonSize;
  /** HTML tag to render as */
  htmlTag?: string;

  // Visual styling
  /** Bold text */
  bold?: boolean;
  /** Rounded corners */
  rounded?: boolean;
  /** Full width button */
  full?: boolean;

  // Icon configuration
  /** Icon element or image configuration */
  icon?: React.ReactElement | IconConfig;
  /** Gap between icon and text */
  iconGap?: IconGap;

  // Carat/arrow indicator
  /** Show a carat/arrow indicator */
  showCarat?: boolean;
  /** Position of the carat */
  caratPosition?: CaratPosition;
  /** Custom carat icon */
  caratIcon?: React.ReactElement;

  // State and interaction
  /** Active state for styling */
  active?: boolean;

  // Input group integration
  /** Hide input border */
  hideInputBorder?: boolean;
  /** Position in input group */
  inputButtonGroupPosition?: string | null;
}

// Type guard to check if icon is a config object or React element
const isIconConfig = (icon: React.ReactElement | IconConfig | null): icon is IconConfig => {
  return icon !== null && typeof icon === 'object' && 'src' in icon;
};

const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      active = false,
      bold = false,
      btnType = 'primary',
      caratIcon = null,
      caratPosition = 'end',
      children = null,
      className = '',
      disabled = false,
      full = false,
      hideInputBorder = false,
      htmlTag = 'button',
      icon = null,
      iconGap = 'sm',
      inputButtonGroupPosition = null,
      onClick,
      rounded = false,
      showCarat = false,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const rest = omit(props, ['dispatch']);

    const Component = htmlTag as React.ElementType;

    const buttonClassName = buttonStyles({
      size,
      btnType,
      full,
      bold,
      disabled,
      rounded,
      active,
      inputButtonGroupPosition: inputButtonGroupPosition as 'before' | 'after' | undefined,
      hideInputBorder,
    });

    const containerClassName = buttonContentContainerStyles({ iconGap, showCarat: showCarat || false });

    const combinedClassName = className ? `${buttonClassName} ${className}` : buttonClassName;

    const renderIcon = () => {
      if (!icon) return null;

      if (isIconConfig(icon)) {
        return (
          <img
            src={icon.src}
            width={icon.width || '16px'}
            height={icon.height || '16px'}
            alt={icon.alt || ''}
            loading={icon.loading || 'lazy'}
            fetchpriority={icon.fetchpriority}
            decoding={icon.decoding}
          />
        );
      }

      return icon;
    };

    const renderCarat = () => {
      if (!showCarat) return null;

      const isExpanded = props['aria-expanded'] === true || props['aria-expanded'] === 'true';
      const caratClassName = caratStyles({ expanded: isExpanded });

      if (caratIcon) {
        return <span className={caratClassName}>{caratIcon}</span>;
      }

      return (
        <img
          className={caratClassName}
          src={IconArrowGrey}
          width="12px"
          height="12px"
          alt=""
          aria-hidden="true"
          fetchpriority="low"
          decoding="async"
        />
      );
    };

    const content =
      icon || showCarat ? (
        <div className={containerClassName}>
          {caratPosition === 'start' && renderCarat()}
          {renderIcon()}
          {children}
          {caratPosition === 'end' && renderCarat()}
        </div>
      ) : (
        children
      );

    return (
      <Component
        {...rest}
        ref={ref}
        className={combinedClassName}
        disabled={disabled}
        aria-disabled={disabled ? 'true' : undefined}
        onClick={onClick}
      >
        {content}
      </Component>
    );
  },
);

Button.displayName = 'Button';

export type { ButtonProps, ButtonType, ButtonSize, IconGap, IconConfig, CaratPosition };
export default Button;
